/**
 * Crafted Beards — shared scroll reveals + mouse/center glow
 * Expects window.gsap + window.ScrollTrigger when motion is allowed.
 * bust: 2026-08-07-mobile-reveal-v2
 */
(function (global) {
  "use strict";

  var reduceMotionMq = window.matchMedia("(prefers-reduced-motion: reduce)");
  var finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
  var touchUiMq = window.matchMedia("(hover: none), (pointer: coarse), (max-width: 959px)");
  var glowInited = false;
  var revealsInited = false;
  var loadRefreshBound = false;

  var SKIP_REVEAL_SEL =
    ".dg-home-sequence, #dgHomeSequence, .sticky-atc, .dg-sticky-atc, cart-drawer, .cart-drawer, #CartDrawer, .jdgm-widget-modal, .jdgm-modal, [data-crafted-motion-skip]";

  function reduceMotion() {
    return reduceMotionMq.matches;
  }

  function touchUi() {
    return touchUiMq.matches;
  }

  function showRevealsImmediate() {
    document.querySelectorAll(".reveal").forEach(function (el) {
      el.style.opacity = "1";
      el.style.transform = "none";
      el.style.filter = "none";
      el.style.clipPath = "none";
    });
  }

  function shouldSkipReveal(el) {
    if (!el || el.getAttribute("data-crafted-reveal") === "1") return true;
    try {
      if (el.closest && el.closest(SKIP_REVEAL_SEL)) return true;
    } catch (err) {
      /* older Safari without :is — ignore */
    }
    return false;
  }

  function groupDelay(el, fallbackIndex, isTouch) {
    var parent = el.parentElement;
    if (!parent) return (fallbackIndex % 4) * (isTouch ? 0.08 : 0.05);
    /* Prefer direct .reveal children for card grids */
    var direct = parent.querySelectorAll(":scope > .reveal");
    if (!direct || direct.length < 2) {
      return (fallbackIndex % 4) * (isTouch ? 0.08 : 0.05);
    }
    var idx = Array.prototype.indexOf.call(direct, el);
    if (idx < 0) idx = fallbackIndex % direct.length;
    return Math.min(idx, 5) * (isTouch ? 0.09 : 0.06);
  }

  function initReveals() {
    if (reduceMotion() || !global.gsap || !global.ScrollTrigger) {
      showRevealsImmediate();
      return;
    }

    if (revealsInited) {
      if (typeof global.ScrollTrigger.refresh === "function") {
        global.ScrollTrigger.refresh();
      }
      return;
    }

    global.gsap.registerPlugin(global.ScrollTrigger);
    if (typeof global.ScrollTrigger.config === "function") {
      global.ScrollTrigger.config({ ignoreMobileResize: true });
    }

    var isTouch = touchUi();

    /*
      Mobile used to start at top 92% with ~28px travel — animation finished
      at the bottom edge before content was readable (“just shows up”).
      Start later + bigger travel so fade/rise is seen mid-viewport; reverse
      as items leave toward the top.
    */
    var fromPresets = {
      up: {
        opacity: 0,
        y: isTouch ? 52 : 40,
        x: 0,
        scale: isTouch ? 0.94 : 1,
        filter: "blur(0px)",
        clipPath: "inset(0% 0% 0% 0%)"
      },
      left: {
        opacity: 0,
        x: isTouch ? 0 : -48,
        y: isTouch ? 44 : 0,
        scale: isTouch ? 0.95 : 1
      },
      right: {
        opacity: 0,
        x: isTouch ? 0 : 48,
        y: isTouch ? 44 : 0,
        scale: isTouch ? 0.95 : 1
      },
      scale: {
        opacity: 0,
        scale: isTouch ? 0.9 : 0.88,
        y: isTouch ? 36 : 0,
        filter: isTouch ? "blur(0px)" : "blur(6px)"
      },
      clip: {
        opacity: 0,
        clipPath: isTouch ? "inset(10% 5% 10% 5%)" : "inset(12% 8% 12% 8%)",
        y: isTouch ? 36 : 18,
        scale: isTouch ? 0.96 : 1
      }
    };

    var toPresets = {
      up: {
        opacity: 1,
        y: 0,
        x: 0,
        scale: 1,
        filter: "blur(0px)",
        clipPath: "inset(0% 0% 0% 0%)",
        duration: isTouch ? 0.85 : 0.75,
        ease: isTouch ? "power2.out" : "power3.out"
      },
      left: {
        opacity: 1,
        x: 0,
        y: 0,
        scale: 1,
        duration: isTouch ? 0.85 : 0.8,
        ease: "power2.out"
      },
      right: {
        opacity: 1,
        x: 0,
        y: 0,
        scale: 1,
        duration: isTouch ? 0.85 : 0.8,
        ease: "power2.out"
      },
      scale: {
        opacity: 1,
        scale: 1,
        filter: "blur(0px)",
        y: 0,
        duration: isTouch ? 0.9 : 0.85,
        ease: "power2.out"
      },
      clip: {
        opacity: 1,
        clipPath: "inset(0% 0% 0% 0%)",
        y: 0,
        scale: 1,
        duration: isTouch ? 0.9 : 0.95,
        ease: "power2.inOut"
      }
    };

    var nodes = global.gsap.utils.toArray(".reveal");
    var staggerIndex = 0;

    nodes.forEach(function (el) {
      if (shouldSkipReveal(el)) return;

      var type = "up";
      if (el.classList.contains("reveal--left")) type = "left";
      else if (el.classList.contains("reveal--right")) type = "right";
      else if (el.classList.contains("reveal--scale")) type = "scale";
      else if (el.classList.contains("reveal--clip")) type = "clip";

      var from = fromPresets[type];
      var to = Object.assign({}, toPresets[type], {
        delay: groupDelay(el, staggerIndex, isTouch),
        overwrite: "auto"
      });
      staggerIndex += 1;

      el.setAttribute("data-crafted-reveal", "1");
      global.gsap.set(el, from);

      /*
        Bidirectional: play on enter, reverse on leave (either direction)
        so scrolling feels alive — not a one-shot pop at the bottom edge.
      */
      global.gsap.fromTo(
        el,
        from,
        Object.assign({}, to, {
          scrollTrigger: {
            trigger: el,
            start: isTouch ? "top 78%" : "top 86%",
            /* Reverse once the block has mostly left toward the top */
            end: isTouch ? "bottom 18%" : "bottom top",
            toggleActions: "play reverse play reverse",
            invalidateOnRefresh: true,
            fastScrollEnd: true
          }
        })
      );
    });

    revealsInited = true;

    if (!loadRefreshBound) {
      loadRefreshBound = true;
      window.addEventListener(
        "load",
        function () {
          if (global.ScrollTrigger) global.ScrollTrigger.refresh();
        },
        { once: true }
      );
    }
  }

  function setGlowPos(el, clientX, clientY) {
    var rect = el.getBoundingClientRect();
    var x = ((clientX - rect.left) / rect.width) * 100;
    var y = ((clientY - rect.top) / rect.height) * 100;
    el.style.setProperty("--mx", x + "%");
    el.style.setProperty("--my", y + "%");
  }

  function initGlow() {
    if (reduceMotion() || glowInited) return;
    /* Skip scroll-linked glow on touch — major mobile jank source */
    if (touchUi()) return;

    var glowTargets = Array.prototype.slice.call(
      document.querySelectorAll(".glow-target")
    );
    if (!glowTargets.length) return;
    glowInited = true;

    function clearGlows(except) {
      glowTargets.forEach(function (el) {
        if (el !== except) el.classList.remove("is-glowing");
      });
    }

    if (finePointer.matches) {
      glowTargets.forEach(function (el) {
        el.addEventListener("pointerenter", function () {
          clearGlows(el);
          el.classList.add("is-glowing");
        });
        el.addEventListener("pointermove", function (e) {
          setGlowPos(el, e.clientX, e.clientY);
        });
        el.addEventListener("pointerleave", function () {
          el.classList.remove("is-glowing");
        });
      });
      return;
    }

    var ticking = false;
    function updateCenterGlow() {
      ticking = false;
      var midY = window.innerHeight * 0.45;
      var best = null;
      var bestDist = Infinity;
      glowTargets.forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.bottom < 80 || r.top > window.innerHeight - 80) return;
        var cy = r.top + r.height / 2;
        var dist = Math.abs(cy - midY);
        if (dist < bestDist) {
          bestDist = dist;
          best = el;
        }
      });
      glowTargets.forEach(function (el) {
        var on = el === best;
        el.classList.toggle("is-glowing", on);
        if (on) {
          el.style.setProperty("--mx", "50%");
          el.style.setProperty("--my", "40%");
        }
      });
    }
    function onScroll() {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(updateCenterGlow);
      }
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    updateCenterGlow();
  }

  function whenGsapReady(fn) {
    if (global.gsap && global.ScrollTrigger) {
      fn();
      return;
    }
    var tries = 0;
    var id = setInterval(function () {
      tries += 1;
      if ((global.gsap && global.ScrollTrigger) || tries > 50) {
        clearInterval(id);
        fn();
      }
    }, 40);
  }

  function init(options) {
    options = options || {};
    var root = options.root || document;
    if (root !== document) {
      /* selectors stay document-wide for theme parity */
    }
    whenGsapReady(function () {
      initReveals();
      initGlow();
    });
  }

  global.CraftedMotion = {
    init: init,
    initReveals: initReveals,
    initGlow: initGlow
  };
})(typeof window !== "undefined" ? window : this);
